import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  FileImage,
  Upload,
  Crop,
  X,
  Check,
  Eye,
  RefreshCw,
  Camera,
  FileText,
} from "lucide-react";
import type { UploadFile, RcFile } from "antd/es/upload/interface";
import { useAppSelector } from "../../../redux/hooks";
import { validToUpload } from "../fixtures/validToUpload";
import { DocumentCropperModal } from "../inputs/DocumentCropperModal";
import { CropperErrorBoundary } from "../inputs/CropperErrorBoundary";
import { isPdfFile, pdfToImage } from "../../../utils/vault/pdfToImage";
import { cn } from "../../../lib/utils";
import { useBreakpoint } from "../../../hooks/useIsMobile";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import {
  formatUploadLimit,
  MEGABYTE,
} from "../../../utils/vault/verificationUploadLimits";
import {
  MAX_IMAGE_BYTES,
  SIMPLE_DOCUMENT_MAX_FILE_SIZE_MB,
} from "../../../constants/uploadPolicies";

const ALLOWED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const ACCEPTED_IMAGES = "image/jpeg,image/png,image/webp";
const LARGE_PDF_PREVIEW_LIMIT_BYTES = 20 * 1024 * 1024;

const formatFileSize = (sizeInBytes: number): string => {
  const sizeInMegabytes = sizeInBytes / MEGABYTE;
  const fractionDigits = Number.isInteger(sizeInMegabytes) ? 0 : 1;
  return `${sizeInMegabytes.toFixed(fractionDigits)} MB`;
};

interface DocumentUploadCardProps {
  name: string;
  label: string;
  type?: "image" | "pdf" | "both";
  enableCropper?: boolean;
  maxSizeMB?: number;
  onFileChange: (files: UploadFile[]) => void;
  value: UploadFile[];
  sideIndicator?: "front" | "back" | null;
  onRawFileSelect?: (file: File) => boolean | Promise<boolean>;
  fileInputRef?: React.MutableRefObject<HTMLInputElement | null>;
}

const acceptMap: Record<string, string> = {
  image: ACCEPTED_IMAGES,
  pdf: "application/pdf,.pdf",
  both: `${ACCEPTED_IMAGES},application/pdf,.pdf`,
};

const validateFile = (
  file: File,
  maxPdfSize: number,
  maxImageSize: number,
  type: "image" | "pdf" | "both",
): string | null => {
  const isImage = ALLOWED_IMAGE_TYPES.has(file.type);
  const isPdf = isPdfFile(file);

  if (file.type.startsWith("image/") && !isImage) {
    return "Formato no compatible. Usa JPG, JPEG, PNG o WebP";
  }

  if (type === "image" && !isImage) return "Solo se permiten imágenes";
  if (type === "pdf" && !isPdf) return "Solo se permiten PDFs";
  if (type === "both" && !isImage && !isPdf) return "Solo imágenes o PDFs";

  const applicableMaxSize = isImage ? maxImageSize : maxPdfSize;
  if (file.size > applicableMaxSize) {
    return `Archivo muy grande. Máximo: ${formatUploadLimit(applicableMaxSize)}`;
  }
  return null;
};

export const DocumentUploadCard: React.FC<DocumentUploadCardProps> = ({
  name: _name,
  label,
  type = "both",
  enableCropper = true,
  maxSizeMB = SIMPLE_DOCUMENT_MAX_FILE_SIZE_MB,
  onFileChange,
  value = [],
  sideIndicator = null,
  onRawFileSelect,
  fileInputRef: externalFileInputRef,
}) => {
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const internalFileInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = externalFileInputRef ?? internalFileInputRef;

  const { activeRequirement } = useAppSelector((state) => state.vault);
  const enabled = validToUpload.includes(activeRequirement?.status);

  const allowed = acceptMap[type];
  const maxPdfSize = maxSizeMB * MEGABYTE;
  const maxImageSize = Math.min(maxSizeMB * MEGABYTE, MAX_IMAGE_BYTES);
  const limitLabel = (() => {
    if (type === "image") return formatUploadLimit(maxImageSize);
    if (type === "pdf") return formatUploadLimit(maxPdfSize);
    if (maxImageSize === maxPdfSize) return formatUploadLimit(maxPdfSize);
    return `Imágenes: ${formatUploadLimit(maxImageSize)} · PDF: ${formatUploadLimit(maxPdfSize)}`;
  })();

  const currentFile = value[0];
  const hasFile = !!currentFile;
  const currentFileSize = currentFile?.size ?? currentFile?.originFileObj?.size;

  // Detect if on mobile/touch device
  const { isTouch } = useBreakpoint();

  // Generate preview URL for the uploaded file (handles both images and PDFs)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const canPreview = previewUrl !== null;

  useEffect(() => {
    const file = currentFile?.originFileObj;
    if (!file) {
      setPreviewUrl(null);
      return;
    }

    let revoke: (() => void) | null = null;
    let cancelled = false;

    if (isPdfFile(file) && file.size <= LARGE_PDF_PREVIEW_LIMIT_BYTES) {
      pdfToImage(file, 1.5)
        .then((dataUrl) => {
          if (!cancelled) setPreviewUrl(dataUrl);
        })
        .catch(() => {
          if (!cancelled) setPreviewUrl(null);
        });
    } else if (isPdfFile(file)) {
      setPreviewUrl(null);
    } else {
      const url = URL.createObjectURL(file);
      revoke = () => URL.revokeObjectURL(url);
      setPreviewUrl(url);
    }

    return () => {
      cancelled = true;
      revoke?.();
    };
  }, [currentFile?.originFileObj]);

  const shouldCrop = useCallback(
    (file: File): boolean => {
      if (!enableCropper) return false;
      return (
        file.type.startsWith("image/") ||
        (isPdfFile(file) && file.size <= LARGE_PDF_PREVIEW_LIMIT_BYTES)
      );
    },
    [enableCropper],
  );

  const handleFileSelect = useCallback(
    async (file: File) => {
      setError(null);
      const validationError = validateFile(
        file,
        maxPdfSize,
        maxImageSize,
        type,
      );
      if (validationError) {
        setError(validationError);
        return;
      }

      let wasHandled = false;
      try {
        wasHandled = (await onRawFileSelect?.(file)) ?? false;
      } catch {
        setError("No se pudo procesar el archivo. Intenta nuevamente.");
        return;
      }

      if (wasHandled) return;

      if (shouldCrop(file)) {
        setPendingFile(file);
        setIsCropperOpen(true);
      } else {
        const uploadFile: UploadFile = {
          uid: `file-${Date.now()}`,
          name: file.name,
          status: "done",
          originFileObj: file as RcFile,
          size: file.size,
          type: file.type,
        };
        onFileChange([uploadFile]);
      }
    },
    [maxPdfSize, maxImageSize, type, onRawFileSelect, shouldCrop, onFileChange],
  );

  const handleCropComplete = useCallback(
    (croppedFile: File) => {
      const uploadFile: UploadFile = {
        uid: `crop-${Date.now()}`,
        name: croppedFile.name,
        status: "done",
        originFileObj: croppedFile as RcFile,
        size: croppedFile.size,
        type: croppedFile.type,
      };
      onFileChange([uploadFile]);
      setPendingFile(null);
      setIsCropperOpen(false);
    },
    [onFileChange],
  );

  const handleCropCancel = useCallback(() => {
    setPendingFile(null);
    setIsCropperOpen(false);
  }, []);

  const handleRemoveFile = useCallback(() => {
    onFileChange([]);
  }, [onFileChange]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
      if (!enabled) return;
      const files = e.dataTransfer.files;
      if (files?.[0]) void handleFileSelect(files[0]);
    },
    [enabled, handleFileSelect],
  );

  const handleClick = useCallback(() => {
    if (!enabled) return;
    fileInputRef.current?.click();
  }, [enabled, fileInputRef]);

  const handleCameraClick = useCallback(() => {
    if (!enabled) return;
    cameraInputRef.current?.click();
  }, [enabled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files?.[0]) void handleFileSelect(files[0]);
      e.target.value = "";
    },
    [handleFileSelect],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const sideLabels = {
    front: "Frente",
    back: "Reverso",
  };

  return (
    <>
      {/* Hidden inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept={allowed}
        onChange={handleInputChange}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept={ACCEPTED_IMAGES}
        capture="environment"
        onChange={handleInputChange}
        className="hidden"
      />

      {/* Cropper Modal */}
      <CropperErrorBoundary
        onReset={() => {
          setPendingFile(null);
          setIsCropperOpen(false);
        }}
        fallbackMessage="Error al procesar el documento."
      >
        <DocumentCropperModal
          isOpen={isCropperOpen}
          onOpenChange={setIsCropperOpen}
          file={pendingFile}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      </CropperErrorBoundary>

      {/* Preview Modal */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-gray-100">
              <img
                src={previewUrl}
                alt={label}
                className="h-full w-full object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Main Card */}
      <div className="flex flex-col">
        {/* Side Label */}
        {sideIndicator && (
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {sideLabels[sideIndicator]}
            </span>
            {hasFile && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Check className="h-3 w-3" />
                Listo
              </span>
            )}
          </div>
        )}

        {/* Card Content */}
        {!hasFile ? (
          /* Dropzone State */
          <div
            role="button"
            tabIndex={enabled ? 0 : -1}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={cn(
              "relative flex flex-col items-center justify-center gap-3",
              "rounded-lg border-2 border-dashed",
              isTouch ? "min-h-[150px] p-4" : "min-h-[180px] p-6",
              "transition-all duration-150",
              "focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2",
              isDragActive
                ? "border-gray-400 bg-gray-100"
                : "border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100",
              !enabled && "cursor-not-allowed opacity-50",
              enabled && "cursor-pointer",
            )}
          >
            {/* Upload Icon */}
            <div
              className={cn(
                "flex h-12 w-12 items-center justify-center rounded-lg",
                "bg-white border border-gray-200",
              )}
            >
              {isDragActive ? (
                <Upload className="h-5 w-5 text-gray-500" />
              ) : (
                <FileImage className="h-5 w-5 text-gray-400" />
              )}
            </div>

            {/* Text Instructions */}
            <div className="text-center">
              <p className="text-sm text-gray-600">
                {isTouch
                  ? "Toca para subir"
                  : isDragActive
                    ? "Suelta aquí"
                    : "Arrastra o haz clic"}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Límite por archivo: {limitLabel}
              </p>
            </div>

            {/* Action Buttons */}
            {!isDragActive && enabled && isTouch && (
              <div className="flex items-center gap-2 w-full justify-center mt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCameraClick();
                  }}
                  className="gap-1.5 text-xs h-11 flex-1 max-w-[140px]"
                >
                  <Camera className="h-4 w-4" />
                  Cámara
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick();
                  }}
                  className="gap-1.5 text-xs h-11 flex-1 max-w-[140px]"
                >
                  <FileImage className="h-4 w-4" />
                  Galería
                </Button>
              </div>
            )}
            {!isDragActive && enabled && !isTouch && enableCropper && (
              <span className="text-[10px] text-gray-400 flex items-center gap-1">
                <Crop className="h-3 w-3" />
                Recorte automático
              </span>
            )}
          </div>
        ) : (
          /* Preview State */
          <div className="relative group rounded-lg overflow-hidden bg-gray-100 border border-gray-200 min-h-[180px]">
            {/* Thumbnail */}
            <div
              className={cn(
                "min-h-[180px] relative",
                canPreview && "cursor-pointer",
              )}
              onClick={canPreview ? () => setIsPreviewOpen(true) : undefined}
              onKeyDown={
                canPreview
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setIsPreviewOpen(true);
                      }
                    }
                  : undefined
              }
              role={canPreview ? "button" : undefined}
              tabIndex={canPreview ? 0 : undefined}
              aria-label={
                canPreview ? `Vista previa de ${currentFile.name}` : undefined
              }
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={label}
                  className="w-full h-auto object-contain"
                />
              ) : (
                <div className="flex min-h-[180px] w-full items-center justify-center gap-3 bg-gray-50 px-6">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white">
                    <FileText className="h-6 w-6 text-gray-500" />
                  </div>
                  <div className="min-w-0">
                    <p
                      className="truncate text-sm font-medium text-gray-800"
                      title={currentFile.name}
                    >
                      {currentFile.name}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                      {currentFileSize !== undefined && (
                        <span>{formatFileSize(currentFileSize)}</span>
                      )}
                      <span>Límite: {limitLabel}</span>
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <Check className="h-3 w-3" />
                        Listo
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Hover overlay */}
              {canPreview && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-colors group-hover:bg-black/40 group-hover:opacity-100">
                  <Eye className="h-5 w-5 text-white" />
                </div>
              )}
            </div>

            {/* Actions bar */}
            {enabled && (
              <div
                className={cn(
                  "absolute top-2 right-2 flex gap-1 transition-opacity",
                  isTouch
                    ? ""
                    : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100",
                )}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick();
                  }}
                  className={cn(
                    "flex h-7 w-7 items-center justify-center rounded bg-white/90 text-gray-600 transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2",
                    isTouch && "h-10 w-10",
                  )}
                  aria-label="Cambiar"
                  title="Cambiar"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile();
                  }}
                  className={cn(
                    "flex h-7 w-7 items-center justify-center rounded bg-white/90 text-gray-600 transition-colors hover:bg-white hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2",
                    isTouch && "h-10 w-10",
                  )}
                  aria-label="Eliminar"
                  title="Eliminar"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Error message */}
        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      </div>
    </>
  );
};
